import * as tslib_1 from "tslib";
import { isEqual, tryFunctionOrLogError, cloneDeep } from 'apollo-utilities';
import { NetworkStatus, isNetworkRequestInFlight } from './networkStatus';
import { Observable } from '../util/Observable';
import { ApolloError } from '../errors/ApolloError';
import { FetchType } from './types';
export var hasError = function hasError(storeValue, policy) {
  if (policy === void 0) {
    policy = 'none';
  }

  return storeValue && (storeValue.graphQLErrors && storeValue.graphQLErrors.length > 0 && policy === 'none' || storeValue.networkError);
};

var ObservableQuery = function (_super) {
  tslib_1.__extends(ObservableQuery, _super);

  function ObservableQuery(_a) {
    var scheduler = _a.scheduler,
        options = _a.options,
        _b = _a.shouldSubscribe,
        shouldSubscribe = _b === void 0 ? true : _b;

    var _this = _super.call(this, function (observer) {
      return _this.onSubscribe(observer);
    }) || this;

    _this.isCurrentlyPolling = false;
    _this.isTornDown = false;
    _this.options = options;
    _this.variables = options.variables || {};
    _this.queryId = scheduler.queryManager.generateQueryId();
    _this.shouldSubscribe = shouldSubscribe;
    _this.scheduler = scheduler;
    _this.queryManager = scheduler.queryManager;
    _this.observers = [];
    _this.subscriptionHandles = [];
    return _this;
  }

  ObservableQuery.prototype.result = function () {
    var that = this;
    return new Promise(function (resolve, reject) {
      var subscription;
      var observer = {
        next: function next(result) {
          resolve(result);

          if (!that.observers.some(function (obs) {
            return obs !== observer;
          })) {
            that.queryManager.removeQuery(that.queryId);
          }

          setTimeout(function () {
            subscription.unsubscribe();
          }, 0);
        },
        error: function error(_error) {
          reject(_error);
        }
      };
      subscription = that.subscribe(observer);
    });
  };

  ObservableQuery.prototype.currentResult = function () {
    if (this.isTornDown) {
      return {
        data: this.lastError ? {} : this.lastResult ? this.lastResult.data : {},
        error: this.lastError,
        loading: false,
        networkStatus: NetworkStatus.error
      };
    }

    var queryStoreValue = this.queryManager.queryStore.get(this.queryId);

    if (hasError(queryStoreValue, this.options.errorPolicy)) {
      return {
        data: {},
        loading: false,
        networkStatus: queryStoreValue.networkStatus,
        error: new ApolloError({
          graphQLErrors: queryStoreValue.graphQLErrors,
          networkError: queryStoreValue.networkError
        })
      };
    }

    var _a = this.queryManager.getCurrentQueryResult(this),
        data = _a.data,
        partial = _a.partial;

    var queryLoading = !queryStoreValue || queryStoreValue.networkStatus === NetworkStatus.loading;
    var loading = this.options.fetchPolicy === 'network-only' && queryLoading || partial && this.options.fetchPolicy !== 'cache-only';
    var networkStatus;

    if (queryStoreValue) {
      networkStatus = queryStoreValue.networkStatus;
    } else {
      networkStatus = loading ? NetworkStatus.loading : NetworkStatus.ready;
    }

    var result = {
      data: data,
      loading: isNetworkRequestInFlight(networkStatus),
      networkStatus: networkStatus
    };

    if (queryStoreValue && queryStoreValue.graphQLErrors && this.options.errorPolicy === 'all') {
      result.errors = queryStoreValue.graphQLErrors;
    }

    if (!partial) {
      this.lastResult = tslib_1.__assign({}, result, {
        stale: false
      });
      this.lastResultSnapshot = cloneDeep(this.lastResult);
    }

    return tslib_1.__assign({}, result, {
      partial: partial
    });
  };

  ObservableQuery.prototype.isDifferentFromLastResult = function (newResult) {
    var snapshot = this.lastResultSnapshot;
    return !(snapshot && newResult && snapshot.networkStatus === newResult.networkStatus && snapshot.stale === newResult.stale && isEqual(snapshot.data, newResult.data));
  };

  ObservableQuery.prototype.getLastResult = function () {
    return this.lastResult;
  };

  ObservableQuery.prototype.getLastError = function () {
    return this.lastError;
  };

  ObservableQuery.prototype.resetLastResults = function () {
    delete this.lastResult;
    delete this.lastResultSnapshot;
    delete this.lastError;
    this.isTornDown = false;
  };

  ObservableQuery.prototype.refetch = function (variables) {
    var fetchPolicy = this.options.fetchPolicy;

    if (fetchPolicy === 'cache-only') {
      return Promise.reject(new Error('cache-only fetchPolicy option should not be used together with query refetch.'));
    }

    if (!isEqual(this.variables, variables)) {
      this.variables = Object.assign({}, this.variables, variables);
    }

    if (!isEqual(this.options.variables, this.variables)) {
      this.options.variables = Object.assign({}, this.options.variables, this.variables);
    }

    var isNetworkFetchPolicy = fetchPolicy === 'network-only' || fetchPolicy === 'no-cache';

    var combinedOptions = tslib_1.__assign({}, this.options, {
      fetchPolicy: isNetworkFetchPolicy ? fetchPolicy : 'network-only'
    });

    return this.queryManager.fetchQuery(this.queryId, combinedOptions, FetchType.refetch).then(function (result) {
      return result;
    });
  };

  ObservableQuery.prototype.fetchMore = function (fetchMoreOptions) {
    var _this = this;

    if (!fetchMoreOptions.updateQuery) {
      throw new Error('updateQuery option is required. This function defines how to update the query data with the new results.');
    }

    var combinedOptions;
    return Promise.resolve().then(function () {
      var qid = _this.queryManager.generateQueryId();

      if (fetchMoreOptions.query) {
        combinedOptions = fetchMoreOptions;
      } else {
        combinedOptions = tslib_1.__assign({}, _this.options, fetchMoreOptions, {
          variables: Object.assign({}, _this.variables, fetchMoreOptions.variables)
        });
      }

      combinedOptions.fetchPolicy = 'network-only';
      return _this.queryManager.fetchQuery(qid, combinedOptions, FetchType.normal, _this.queryId);
    }).then(function (fetchMoreResult) {
      _this.updateQuery(function (previousResult) {
        return fetchMoreOptions.updateQuery(previousResult, {
          fetchMoreResult: fetchMoreResult.data,
          variables: combinedOptions.variables
        });
      });

      return fetchMoreResult;
    });
  };

  ObservableQuery.prototype.subscribeToMore = function (options) {
    var _this = this;

    var subscription = this.queryManager.startGraphQLSubscription({
      query: options.document,
      variables: options.variables
    }).subscribe({
      next: function next(subscriptionData) {
        if (options.updateQuery) {
          _this.updateQuery(function (previous, _a) {
            var variables = _a.variables;
            return options.updateQuery(previous, {
              subscriptionData: subscriptionData,
              variables: variables
            });
          });
        }
      },
      error: function error(err) {
        if (options.onError) {
          options.onError(err);
          return;
        }

        console.error('Unhandled GraphQL subscription error', err);
      }
    });
    this.subscriptionHandles.push(subscription);
    return function () {
      var i = _this.subscriptionHandles.indexOf(subscription);

      if (i >= 0) {
        _this.subscriptionHandles.splice(i, 1);

        subscription.unsubscribe();
      }
    };
  };

  ObservableQuery.prototype.setOptions = function (opts) {
    var oldOptions = this.options;
    this.options = Object.assign({}, this.options, opts);

    if (opts.pollInterval) {
      this.startPolling(opts.pollInterval);
    } else if (opts.pollInterval === 0) {
      this.stopPolling();
    }

    var tryFetch = oldOptions.fetchPolicy !== 'network-only' && opts.fetchPolicy === 'network-only' || oldOptions.fetchPolicy === 'cache-only' && opts.fetchPolicy !== 'cache-only' || oldOptions.fetchPolicy === 'standby' && opts.fetchPolicy !== 'standby' || false;
    return this.setVariables(this.options.variables, tryFetch, opts.fetchResults);
  };

  ObservableQuery.prototype.setVariables = function (variables, tryFetch, fetchResults) {
    if (tryFetch === void 0) {
      tryFetch = false;
    }

    if (fetchResults === void 0) {
      fetchResults = true;
    }

    this.isTornDown = false;
    var newVariables = variables ? variables : this.variables;

    if (isEqual(newVariables, this.variables) && !tryFetch) {
      if (this.observers.length === 0 || !fetchResults) {
        return new Promise(function (resolve) {
          return resolve();
        });
      }

      return this.result();
    } else {
      this.variables = newVariables;
      this.options.variables = newVariables;

      if (this.observers.length === 0) {
        return new Promise(function (resolve) {
          return resolve();
        });
      }

      return this.queryManager.fetchQuery(this.queryId, tslib_1.__assign({}, this.options, {
        variables: this.variables
      })).then(function (result) {
        return result;
      });
    }
  };

  ObservableQuery.prototype.updateQuery = function (mapFn) {
    var _a = this.queryManager.getQueryWithPreviousResult(this.queryId),
        previousResult = _a.previousResult,
        variables = _a.variables,
        document = _a.document;

    var newResult = tryFunctionOrLogError(function () {
      return mapFn(previousResult, {
        variables: variables
      });
    });

    if (newResult) {
      this.queryManager.dataStore.markUpdateQueryResult(document, variables, newResult);
      this.queryManager.broadcastQueries();
    }
  };

  ObservableQuery.prototype.stopPolling = function () {
    if (this.isCurrentlyPolling) {
      this.scheduler.stopPollingQuery(this.queryId);
      this.options.pollInterval = undefined;
      this.isCurrentlyPolling = false;
    }
  };

  ObservableQuery.prototype.startPolling = function (pollInterval) {
    if (this.options.fetchPolicy === 'cache-first' || this.options.fetchPolicy === 'cache-only') {
      throw new Error('Queries that specify the cache-first and cache-only fetchPolicies cannot also be polling queries.');
    }

    if (this.isCurrentlyPolling) {
      this.scheduler.stopPollingQuery(this.queryId);
      this.isCurrentlyPolling = false;
    }

    this.options.pollInterval = pollInterval;
    this.isCurrentlyPolling = true;
    this.scheduler.startPollingQuery(this.options, this.queryId);
  };

  ObservableQuery.prototype.onSubscribe = function (observer) {
    var _this = this;

    if (observer._subscription && observer._subscription._observer && !observer._subscription._observer.error) {
      observer._subscription._observer.error = function (error) {
        console.error('Unhandled error', error.message, error.stack);
      };
    }

    this.observers.push(observer);
    if (observer.next && this.lastResult) observer.next(this.lastResult);
    if (observer.error && this.lastError) observer.error(this.lastError);
    if (this.observers.length === 1) this.setUpQuery();
    return function () {
      _this.observers = _this.observers.filter(function (obs) {
        return obs !== observer;
      });

      if (_this.observers.length === 0) {
        _this.tearDownQuery();
      }
    };
  };

  ObservableQuery.prototype.setUpQuery = function () {
    var _this = this;

    if (this.shouldSubscribe) {
      this.queryManager.addObservableQuery(this.queryId, this);
    }

    if (!!this.options.pollInterval) {
      if (this.options.fetchPolicy === 'cache-first' || this.options.fetchPolicy === 'cache-only') {
        throw new Error('Queries that specify the cache-first and cache-only fetchPolicies cannot also be polling queries.');
      }

      this.isCurrentlyPolling = true;
      this.scheduler.startPollingQuery(this.options, this.queryId);
    }

    var observer = {
      next: function next(result) {
        _this.lastResult = result;
        _this.lastResultSnapshot = cloneDeep(result);

        _this.observers.forEach(function (obs) {
          return obs.next && obs.next(result);
        });
      },
      error: function error(_error2) {
        _this.lastError = _error2;

        _this.observers.forEach(function (obs) {
          return obs.error && obs.error(_error2);
        });
      }
    };
    this.queryManager.startQuery(this.queryId, this.options, this.queryManager.queryListenerForObserver(this.queryId, this.options, observer));
  };

  ObservableQuery.prototype.tearDownQuery = function () {
    this.isTornDown = true;

    if (this.isCurrentlyPolling) {
      this.scheduler.stopPollingQuery(this.queryId);
      this.isCurrentlyPolling = false;
    }

    this.subscriptionHandles.forEach(function (sub) {
      return sub.unsubscribe();
    });
    this.subscriptionHandles = [];
    this.queryManager.removeObservableQuery(this.queryId);
    this.queryManager.stopQuery(this.queryId);
    this.observers = [];
  };

  return ObservableQuery;
}(Observable);

export { ObservableQuery };